import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import type {
  AdminUser,
  AuthUser,
  MfaLoginMethod,
  UserRole,
} from "../api/admin";
import {
  confirmMfaSetup as confirmMfaSetupRequest,
  createUser as createUserRequest,
  deleteUser as deleteUserRequest,
  getCurrentSession,
  listUsers,
  login as loginRequest,
  logout as logoutRequest,
  regenerateMfaRecoveryCodes as regenerateMfaRecoveryCodesRequest,
  resetUserMfa as resetUserMfaRequest,
  updateUser as updateUserRequest,
  verifyMfaLogin,
} from "../api/admin";
import { ApiError } from "../api/client";

export type LoginActionResult =
  | { success: true; status: "authenticated" }
  | {
      success: true;
      status: "mfa_required";
      challengeId: string;
      methods: MfaLoginMethod[];
      expiresInSeconds: number;
    }
  | {
      success: true;
      status: "mfa_setup_required";
      challengeId: string;
      otpauthUrl: string;
      manualKey: string;
      expiresInSeconds: number;
    }
  | { success: false; error?: string };

export type MfaActionResult =
  | { success: true; recoveryCodes?: string[]; recoveryCodesRemaining?: number | null }
  | { success: false; error?: string };

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  bootstrapError: string | null;
  login: (email: string, password: string) => Promise<LoginActionResult>;
  verifyMfa: (challengeId: string, method: MfaLoginMethod, code: string) => Promise<MfaActionResult>;
  confirmMfaSetup: (challengeId: string, code: string) => Promise<MfaActionResult>;
  regenerateMfaRecoveryCodes: (password: string, mfaCode: string) => Promise<MfaActionResult>;
  logout: () => Promise<void>;
  refreshUsers: () => Promise<AdminUser[]>;
  createUser: (data: { email: string; name: string; password: string; role: UserRole }) => Promise<{ success: boolean; error?: string }>;
  updateUser: (id: string, data: Partial<Pick<AuthUser, "name" | "role">>) => Promise<{ success: boolean; error?: string }>;
  deleteUser: (id: string) => Promise<{ success: boolean; error?: string }>;
  resetUserMfa: (id: string, data: { adminPassword: string; adminMfaCode: string }) => Promise<{ success: boolean; error?: string }>;
  reloadSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  const reloadSession = useCallback(async () => {
    setIsBootstrapping(true);
    try {
      const session = await getCurrentSession();
      setUser(session);
      setBootstrapError(null);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        setUser(null);
        setBootstrapError(null);
        return;
      }
      setUser(null);
      setBootstrapError(error instanceof Error ? error.message : "Failed to reach backend");
    } finally {
      setIsBootstrapping(false);
    }
  }, []);

  useEffect(() => {
    void reloadSession();
  }, [reloadSession]);

  const login = useCallback(async (email: string, password: string): Promise<LoginActionResult> => {
    try {
      const result = await loginRequest(email, password);
      if (result.status === "authenticated") {
        setUser(result.session);
        setBootstrapError(null);
        return { success: true, status: "authenticated" } satisfies LoginActionResult;
      }
      return { success: true, ...result } satisfies LoginActionResult;
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Login failed";
      return { success: false, error: message };
    }
  }, []);

  const verifyMfa = useCallback(
    async (challengeId: string, method: MfaLoginMethod, code: string): Promise<MfaActionResult> => {
      try {
        const result = await verifyMfaLogin({ challengeId, method, code });
        setUser(result.session);
        setBootstrapError(null);
        return { success: true, recoveryCodesRemaining: result.recoveryCodesRemaining };
      } catch (error) {
        const message = error instanceof ApiError ? error.message : "MFA verification failed";
        return { success: false, error: message };
      }
    },
    [],
  );

  const confirmMfaSetup = useCallback(async (challengeId: string, code: string): Promise<MfaActionResult> => {
    try {
      const result = await confirmMfaSetupRequest({ challengeId, code });
      setUser(result.session);
      setBootstrapError(null);
      return { success: true, recoveryCodes: result.recoveryCodes };
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "MFA setup failed";
      return { success: false, error: message };
    }
  }, []);

  const regenerateMfaRecoveryCodes = useCallback(
    async (password: string, mfaCode: string): Promise<MfaActionResult> => {
      try {
        const recoveryCodes = await regenerateMfaRecoveryCodesRequest({ password, mfaCode });
        return { success: true, recoveryCodes };
      } catch (error) {
        const message = error instanceof ApiError ? error.message : "Failed to regenerate recovery codes";
        return { success: false, error: message };
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch (error) {
      if (!(error instanceof ApiError) || (error.status !== 401 && error.status !== 403)) {
        console.error(error);
      }
    } finally {
      setUser(null);
      setBootstrapError(null);
    }
  }, []);

  const refreshUsers = useCallback(async (): Promise<AdminUser[]> => listUsers(), []);

  const createUser = useCallback(async (data: { email: string; name: string; password: string; role: UserRole }) => {
    try {
      await createUserRequest(data);
      return { success: true };
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Failed to create user";
      return { success: false, error: message };
    }
  }, []);

  const updateUser = useCallback(async (id: string, data: Partial<Pick<AuthUser, "name" | "role">>) => {
    try {
      const updated = await updateUserRequest(id, data);
      if (user?.id === id) {
        setUser({
          id: updated.id,
          email: updated.email,
          name: updated.name,
          role: updated.role,
          createdAt: updated.createdAt,
        });
      }
      return { success: true };
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Failed to update user";
      return { success: false, error: message };
    }
  }, [user]);

  const deleteUser = useCallback(async (id: string) => {
    if (user?.id === id) {
      return { success: false, error: "Cannot delete your own account" };
    }

    try {
      await deleteUserRequest(id);
      return { success: true };
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Failed to delete user";
      return { success: false, error: message };
    }
  }, [user]);

  const resetUserMfa = useCallback(
    async (id: string, data: { adminPassword: string; adminMfaCode: string }) => {
      if (user?.id === id) {
        return { success: false, error: "Cannot reset MFA for your own active session" };
      }

      try {
        await resetUserMfaRequest(id, data);
        return { success: true };
      } catch (error) {
        const message = error instanceof ApiError ? error.message : "Failed to reset MFA";
        return { success: false, error: message };
      }
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isBootstrapping,
      bootstrapError,
      login,
      verifyMfa,
      confirmMfaSetup,
      regenerateMfaRecoveryCodes,
      logout,
      refreshUsers,
      createUser,
      updateUser,
      deleteUser,
      resetUserMfa,
      reloadSession,
    }),
    [
      bootstrapError,
      confirmMfaSetup,
      createUser,
      deleteUser,
      isBootstrapping,
      login,
      logout,
      refreshUsers,
      regenerateMfaRecoveryCodes,
      reloadSession,
      resetUserMfa,
      updateUser,
      user,
      verifyMfa,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
